import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/core';
import { theme } from '../../styles';

// Animation timing constants (must match Timeline.js)
const LINE_DURATION = 600;  // ms
const REVEAL_DURATION = 200;   // ms for each year reveal animation

// Animation for the horizontal line drawing from left to right
const drawLine = keyframes`
  from {
    transform: translateY(-50%) scaleX(0);
  }
  to {
    transform: translateY(-50%) scaleX(1);
  }
`;

// Animation for dots appearing
const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(var(--dot-scale));
  }
`;

// Animation for year labels
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

export const TimelineContainer = styled.div`
  width: 100%;
  padding: 20px 0;
  position: relative;


  @media (min-width: ${theme.breakpoints.md}) {
    padding: 40px 0;
  }
`;

export const TimelineWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 40px 0 20px;

  @media (min-width: ${theme.breakpoints.md}) {
    overflow: visible;
    padding: 40px 20px 20px;
  }
`;

export const TimelineLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  /* Show max 4 items on mobile - each item ~25% width */
  gap: calc((100% - 40px) / 3);
  padding: 0 60px;

  /* Full horizontal line with animation */
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background-color: ${theme.colors.black};
    transform-origin: left center;
    animation: ${drawLine} ${LINE_DURATION}ms ease-out forwards;
  }

  @media (min-width: ${theme.breakpoints.md}) {
    justify-content: center;
    gap: 44px;
    padding: 0;
  }

  @media (min-width: ${theme.breakpoints.lg}) {
    gap: 60px;
  }
`;

export const TimelinePoint = styled.button`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 1;
  cursor: pointer;
  --dot-scale: ${(props) => (props.isSelected ? 1.25 : 0.75)};

  &:focus {
    outline-color: transparent;
  }

  /* Year label - positioned above, revealed via isRevealed prop */
  &::before {
    content: attr(data-year);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding-bottom: 12px;
    font-family: ${theme.fontFamily.primary};
    font-size: ${(props) => (props.isSelected ? '18px' : '12px')};
    font-weight: ${(props) => (props.isSelected ? '700' : '400')};
    color: ${(props) =>
      props.isSelected ? theme.colors.primaryred : theme.colors.black};
    white-space: nowrap;
    transition: color 0.2s ease-out, font-weight 0.2s ease-out, font-size 0.2s ease-out, opacity ${REVEAL_DURATION}ms ease-out, transform ${REVEAL_DURATION}ms ease-out;
    opacity: ${(props) => (props.isRevealed ? 1 : 0)};
    transform: translateX(-50%) translateY(${(props) => (props.isRevealed ? '0' : '5px')});
  }

  &:hover::before {
    font-weight: 600;
  }

  /* Dot on the line - revealed via isRevealed prop */
  &::after {
    content: '';
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) =>
      props.isSelected ? theme.colors.primaryred : theme.colors.black};
    transform: scale(${(props) => (props.isRevealed ? 'var(--dot-scale)' : '0')});
    transition: background-color 0.2s ease-out, transform ${REVEAL_DURATION}ms ease-out;
    opacity: ${(props) => (props.isRevealed ? 1 : 0)};
  }

  &:hover {
    --dot-scale: 0.9;
  }

  /* Selected state - override hover */
  ${(props) =>
    props.isSelected &&
    css`
      &:hover {
        --dot-scale: 1.25;
      }
    `}

`;

export const TimelineDot = styled.div`
  display: none;
`;

export const TimelineYear = styled.span`
  display: none;
`;

export const TimelineConnector = styled.div`
  display: none;
`;

export const NavButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  margin-top: 15px;

  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

export const TimelineNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${theme.colors.greyLight};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background-color: ${theme.colors.grey};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
    color: ${theme.colors.black};
  }

  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

export const MobileTimelineWrapper = styled.div`
  display: block;
  width: 100%;

  @media (min-width: ${theme.breakpoints.md}) {
    display: none;
  }
`;

export const MobileSwiperContainer = styled.div`
  width: 100%;
  padding: 0 20px;
  position: relative;

  /* Horizontal line behind swiper */
  &::before {
    content: '';
    position: absolute;
    bottom: 9px;
    left: 16px;
    right: 16px;
    height: 1px;
    background-color: ${theme.colors.black};
    z-index: 0;
  }

  .swiper {
    overflow: hidden;
  }

  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: center;
    outline: none;
    
    &:focus {
      outline: none;
    }
  }
`;

export const SwiperYearSlide = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px 0 24px 0;
  position: relative;
  z-index: 1;
  outline: none;
  overflow: visible;

  &:focus {
    outline: none;
    border: none;
    box-shadow: none;
  }

  .year-label {
    font-family: ${theme.fontFamily.primary};
    font-weight: ${(props) => (props.isSelected ? '700' : '400')};
    font-size: 16px;
    transform: scale(${(props) => (props.isSelected ? 1 : 0.75)});
    transform-origin: center bottom;
    color: ${(props) =>
      props.isSelected ? theme.colors.primaryred : theme.colors.black};
    margin-bottom: 8px;
    transition: color 0.2s ease-out, font-weight 0.2s ease-out, transform 0.2s ease-out, opacity ${REVEAL_DURATION}ms ease-out;
    min-height: 22px;
    display: flex;
    align-items: flex-end;
    opacity: ${(props) => (props.isRevealed ? 1 : 0)};
  }

  &:hover .year-label {
    font-weight: 600;
  }

  .year-dot {
    position: absolute;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%) scale(${(props) => {
      if (!props.isRevealed) return '0';
      return props.isSelected ? '1.25' : '0.75';
    }});
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${(props) =>
      props.isSelected ? theme.colors.primaryred : theme.colors.black};
    transition: background-color 0.2s ease-out, transform ${REVEAL_DURATION}ms ease-out, opacity ${REVEAL_DURATION}ms ease-out;
    opacity: ${(props) => (props.isRevealed ? 1 : 0)};
  }

  &:hover .year-dot {
    transform: translateX(-50%) scale(0.9);
  }

  ${(props) =>
    props.isSelected &&
    css`
      &:hover .year-dot {
        transform: translateX(-50%) scale(1.25);
      }
    `}
`;

export const DesktopTimelineWrapper = styled.div`
  display: none;

  @media (min-width: ${theme.breakpoints.md}) {
    display: block;
    width: 100%;
  }
`;
